import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Settings, Send, Plus, MessageSquare, Trash2, Loader2, Languages, LogOut, User as UserIcon, Menu, X, ChevronDown, Mic, MicOff, Volume2, VolumeX, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const translations = {
  fr: {
    title: "Abena",
    subtitle: "Assistante Santé Mentale",
    newConversation: "Nouvelle conversation",
    settings: "Paramètres",
    personalization: "Personnalisation",
    creativity: "Créativité",
    tone: "Ton de conversation",
    language: "Langue",
    empathetic: "Empathique",
    professional: "Professionnel",
    encouraging: "Encourageant",
    startConversation: "Commencez une conversation",
    shareThoughts: "Partagez ce qui vous préoccupe, je suis là pour vous écouter.",
    welcome: "Bienvenue sur Abena",
    createNewConv: "Créez une nouvelle conversation pour commencer",
    placeholder: "Partagez vos pensées...",
    conversationCreated: "Nouvelle conversation créée",
    conversationDeleted: "Conversation supprimée",
    settingsUpdated: "Paramètres mis à jour",
    errorSending: "Erreur lors de l'envoi du message",
    errorCreating: "Erreur lors de la création",
    errorDeleting: "Erreur lors de la suppression",
    errorUpdating: "Erreur lors de la mise à jour",
    login: "Connexion",
    signup: "Inscription",
    email: "Email",
    password: "Mot de passe",
    name: "Nom",
    logout: "Déconnexion",
    loginBtn: "Se connecter",
    signupBtn: "S'inscrire",
    haveAccount: "Déjà un compte ?",
    noAccount: "Pas encore de compte ?",
    loginSuccess: "Connecté avec succès",
    signupSuccess: "Compte créé avec succès",
    menu: "Menu"
  },
  en: {
    title: "Abena",
    subtitle: "Mental Health Assistant",
    newConversation: "New conversation",
    settings: "Settings",
    personalization: "Personalization",
    creativity: "Creativity",
    tone: "Conversation tone",
    language: "Language",
    empathetic: "Empathetic",
    professional: "Professional",
    encouraging: "Encouraging",
    startConversation: "Start a conversation",
    shareThoughts: "Share what's on your mind, I'm here to listen.",
    welcome: "Welcome to Abena",
    createNewConv: "Create a new conversation to start",
    placeholder: "Share your thoughts...",
    conversationCreated: "New conversation created",
    conversationDeleted: "Conversation deleted",
    settingsUpdated: "Settings updated",
    errorSending: "Error sending message",
    errorCreating: "Error creating",
    errorDeleting: "Error deleting",
    errorUpdating: "Error updating",
    login: "Login",
    signup: "Sign Up",
    email: "Email",
    password: "Password",
    name: "Name",
    logout: "Logout",
    loginBtn: "Login",
    signupBtn: "Sign Up",
    haveAccount: "Already have an account?",
    noAccount: "Don't have an account?",
    loginSuccess: "Logged in successfully",
    signupSuccess: "Account created successfully",
    menu: "Menu"
  },
  es: {
    title: "Abena",
    subtitle: "Asistente de Salud Mental",
    newConversation: "Nueva conversación",
    settings: "Configuración",
    personalization: "Personalización",
    creativity: "Creatividad",
    tone: "Tono de conversación",
    language: "Idioma",
    empathetic: "Empático",
    professional: "Profesional",
    encouraging: "Alentador",
    startConversation: "Comenzar una conversación",
    shareThoughts: "Comparte lo que te preocupa, estoy aquí para escucharte.",
    welcome: "Bienvenido a Abena",
    createNewConv: "Crea una nueva conversación para comenzar",
    placeholder: "Comparte tus pensamientos...",
    conversationCreated: "Nueva conversación creada",
    conversationDeleted: "Conversación eliminada",
    settingsUpdated: "Configuración actualizada",
    errorSending: "Error al enviar el mensaje",
    errorCreating: "Error al crear",
    errorDeleting: "Error al eliminar",
    errorUpdating: "Error al actualizar",
    login: "Iniciar sesión",
    signup: "Registrarse",
    email: "Correo electrónico",
    password: "Contraseña",
    name: "Nombre",
    logout: "Cerrar sesión",
    loginBtn: "Iniciar sesión",
    signupBtn: "Registrarse",
    haveAccount: "¿Ya tienes una cuenta?",
    noAccount: "¿No tienes cuenta?",
    loginSuccess: "Sesión iniciada con éxito",
    signupSuccess: "Cuenta creada con éxito",
    menu: "Menú"
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ temperature: 0.7, tone: "empathique", language: "fr" });
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);

  const t = translations[settings.language] || translations.fr;

  const axiosConfig = {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (token) {
      checkAuth();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
      loadSettings();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentConversation) {
      loadMessages(currentConversation.id);
      setSidebarOpen(false); // Close sidebar on mobile when conversation is selected
    }
  }, [currentConversation]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, axiosConfig);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setToken(response.data.access_token);
      setUser(response.data.user);
      localStorage.setItem('token', response.data.access_token);
      setIsAuthenticated(true);
      toast.success(t.loginSuccess);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    }
  };

  const handleSignup = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, { email, password, name });
      setToken(response.data.access_token);
      setUser(response.data.user);
      localStorage.setItem('token', response.data.access_token);
      setIsAuthenticated(true);
      toast.success(t.signupSuccess);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setConversations([]);
    setCurrentConversation(null);
    setMessages([]);
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, axiosConfig);
      setConversations(response.data);
      if (response.data.length > 0 && !currentConversation) {
        setCurrentConversation(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/chat/conversations/${conversationId}/messages`, axiosConfig);
      setMessages(response.data);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/chat/settings`, axiosConfig);
      setSettings(response.data);
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await axios.post(`${API}/chat/conversations`, {
        title: `Conversation ${new Date().toLocaleDateString(settings.language === 'es' ? 'es-ES' : settings.language === 'en' ? 'en-US' : 'fr-FR')}`
      }, axiosConfig);
      setConversations([response.data, ...conversations]);
      setCurrentConversation(response.data);
      setMessages([]);
      toast.success(t.conversationCreated);
      setSidebarOpen(false);
    } catch (error) {
      toast.error(t.errorCreating);
    }
  };

  const deleteConversation = async (convId) => {
    try {
      await axios.delete(`${API}/chat/conversations/${convId}`, axiosConfig);
      const updatedConvs = conversations.filter(c => c.id !== convId);
      setConversations(updatedConvs);
      if (currentConversation?.id === convId) {
        setCurrentConversation(updatedConvs[0] || null);
        setMessages([]);
      }
      toast.success(t.conversationDeleted);
    } catch (error) {
      toast.error(t.errorDeleting);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentConversation) return;

    const userMsg = inputMessage;
    setInputMessage("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        conversation_id: currentConversation.id,
        content: userMsg
      }, axiosConfig);

      setMessages([...messages, response.data.user_message, response.data.ai_response]);
    } catch (error) {
      toast.error(t.errorSending);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/chat/settings`, newSettings, axiosConfig);
      setSettings({ ...settings, ...newSettings });
      toast.success(t.settingsUpdated);
    } catch (error) {
      toast.error(t.errorUpdating);
    }
  };

  // Speech-to-Text (Voice Input)
  const toggleRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("La reconnaissance vocale n'est pas supportée sur ce navigateur");
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = settings.language === 'fr' ? 'fr-FR' : settings.language === 'es' ? 'es-ES' : 'en-US';
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onstart = () => {
        setIsRecording(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current.start();
    }
  };

  // Text-to-Speech (Read Response)
  const speakText = (text) => {
    if (!('speechSynthesis' in window)) {
      toast.error("La synthèse vocale n'est pas supportée sur ce navigateur");
      return;
    }

    // Stop any ongoing speech
    window.speechSynthesis.cancel();

    if (isSpeaking) {
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = settings.language === 'fr' ? 'fr-FR' : settings.language === 'es' ? 'es-ES' : 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  };

  // Generate Image
  const generateImage = async (prompt) => {
    if (!currentConversation) return;

    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/chat/generate-image`, {
        conversation_id: currentConversation.id,
        prompt: prompt
      }, axiosConfig);

      // Add image to messages
      const imageMsg = {
        ...response.data.message,
        image_base64: response.data.image_base64
      };
      
      setMessages([...messages, imageMsg]);
      toast.success("Image générée avec succès!");
    } catch (error) {
      toast.error("Erreur lors de la génération de l'image");
      console.error(error);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Handle streaming messages
  const handleMessageWithStreamingDetection = async () => {
    if (!inputMessage.trim() || !currentConversation) return;

    const userMsg = inputMessage;
    const lowerMsg = userMsg.toLowerCase();
    
    // Keywords for image generation
    const imageKeywords = {
      fr: ['génère', 'crée', 'montre', 'image', 'photo', 'illustration', 'dessin'],
      en: ['generate', 'create', 'show', 'image', 'picture', 'illustration', 'draw'],
      es: ['genera', 'crea', 'muestra', 'imagen', 'foto', 'ilustración', 'dibuja']
    };

    const keywords = imageKeywords[settings.language] || imageKeywords.fr;
    const isImageRequest = keywords.some(keyword => lowerMsg.includes(keyword));

    setInputMessage("");
    setLoading(true);

    try {
      if (isImageRequest) {
        // Generate image (no streaming for images)
        const userMessage = {
          id: Date.now().toString(),
          conversation_id: currentConversation.id,
          role: "user",
          content: userMsg,
          timestamp: new Date().toISOString()
        };
        
        setMessages([...messages, userMessage]);
        await generateImage(userMsg);
      } else {
        // Stream text response
        const userMessage = {
          id: Date.now().toString(),
          conversation_id: currentConversation.id,
          role: "user",
          content: userMsg,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, userMessage]);
        
        // Create placeholder for AI message that will be updated
        const aiMessageId = Date.now().toString() + "_ai";
        const aiMessage = {
          id: aiMessageId,
          conversation_id: currentConversation.id,
          role: "assistant",
          content: "",
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, aiMessage]);
        
        // Setup EventSource for streaming
        const response = await fetch(`${API}/chat/message/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            conversation_id: currentConversation.id,
            content: userMsg
          })
        });

        if (!response.ok || !response.body) {
          throw new Error('Streaming response failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamedContent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'content') {
                  streamedContent += data.content;
                  // Update AI message content in real-time
                  setMessages(prev => prev.map(msg => 
                    msg.id === aiMessageId 
                      ? { ...msg, content: streamedContent }
                      : msg
                  ));
                } else if (data.type === 'done') {
                  // Final message received
                  const finalMessage = JSON.parse(data.message);
                  setMessages(prev => prev.map(msg => 
                    msg.id === aiMessageId 
                      ? finalMessage
                      : msg
                  ));
                } else if (data.error) {
                  throw new Error(data.error);
                }
              } catch (parseError) {
                console.error('Parse error:', parseError);
              }
            }
          }
        }
      }
    } catch (error) {
      toast.error(t.errorSending);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <AuthScreen onLogin={handleLogin} onSignup={handleSignup} language={settings.language} setLanguage={(lang) => setSettings({...settings, language: lang})} />;
  }

  return (
    <div className="app-container">
      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="abena-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/ajxw7a5m_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
              alt="Abena" 
              className="abena-avatar"
            />
            <div>
              <h1 className="sidebar-title" data-testid="app-title">{t.title}</h1>
              <p className="sidebar-subtitle">{t.subtitle}</p>
            </div>
          </div>
          {user && (
            <div className="user-info">
              <UserIcon className="w-4 h-4" />
              <span>{user.name}</span>
            </div>
          )}
        </div>

        <Button 
          onClick={createNewConversation} 
          className="new-conversation-btn"
          data-testid="new-conversation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t.newConversation}
        </Button>

        <div className="conversations-list">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`conversation-item ${currentConversation?.id === conv.id ? 'active' : ''}`}
              onClick={() => setCurrentConversation(conv)}
              data-testid={`conversation-item-${conv.id}`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="conversation-title">{conv.title}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(conv.id);
                }}
                className="delete-btn"
                data-testid={`delete-conversation-${conv.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="sidebar-bottom">
          <Button 
            variant="outline" 
            className="settings-btn" 
            onClick={() => {
              setShowSettings(true);
              setSidebarOpen(false);
            }}
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t.settings}
          </Button>

          <Button variant="outline" className="logout-btn" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            {t.logout}
          </Button>
        </div>
      </div>

      {/* Settings Modal for Mobile/Desktop */}
      {showSettings && (
        <>
          <div className="settings-modal-overlay" onClick={() => setShowSettings(false)} />
          <div className="settings-modal" data-testid="settings-modal">
            <div className="settings-modal-header">
              <h2>{t.personalization}</h2>
              <button 
                onClick={() => setShowSettings(false)}
                className="settings-close-btn"
                data-testid="settings-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="settings-modal-content">
              <Accordion type="single" collapsible className="settings-accordion" defaultValue="item-1">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="accordion-trigger">
                    <div className="accordion-title">
                      <Settings className="w-4 h-4" />
                      <span>{t.creativity}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="accordion-content">
                    <div className="setting-item">
                      <label data-testid="creativity-label" className="setting-value">
                        {t.creativity}: <strong>{settings.temperature}</strong>
                      </label>
                      <Slider
                        value={[settings.temperature]}
                        onValueChange={(val) => updateSettings({ temperature: val[0] })}
                        min={0}
                        max={1}
                        step={0.1}
                        className="mt-2"
                        data-testid="temperature-slider"
                      />
                      <div className="slider-labels">
                        <span>0</span>
                        <span>0.5</span>
                        <span>1</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger className="accordion-trigger">
                    <div className="accordion-title">
                      <MessageSquare className="w-4 h-4" />
                      <span>{t.tone}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="accordion-content">
                    <div className="setting-item">
                      <label data-testid="tone-label" className="setting-label">{t.tone}</label>
                      <div className="options-grid">
                        <button
                          onClick={() => updateSettings({ tone: "empathique" })}
                          className={`option-card ${settings.tone === "empathique" ? "active" : ""}`}
                          data-testid="tone-empathique-btn"
                        >
                          <div className="option-icon">💙</div>
                          <div className="option-text">{t.empathetic}</div>
                        </button>
                        <button
                          onClick={() => updateSettings({ tone: "professionnel" })}
                          className={`option-card ${settings.tone === "professionnel" ? "active" : ""}`}
                          data-testid="tone-professionnel-btn"
                        >
                          <div className="option-icon">💼</div>
                          <div className="option-text">{t.professional}</div>
                        </button>
                        <button
                          onClick={() => updateSettings({ tone: "encourageant" })}
                          className={`option-card ${settings.tone === "encourageant" ? "active" : ""}`}
                          data-testid="tone-encourageant-btn"
                        >
                          <div className="option-icon">⭐</div>
                          <div className="option-text">{t.encouraging}</div>
                        </button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger className="accordion-trigger">
                    <div className="accordion-title">
                      <Languages className="w-4 h-4" />
                      <span>{t.language}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="accordion-content">
                    <div className="setting-item">
                      <label data-testid="language-label" className="setting-label">{t.language}</label>
                      <div className="options-grid">
                        <button
                          onClick={() => updateSettings({ language: "fr" })}
                          className={`option-card ${settings.language === "fr" ? "active" : ""}`}
                          data-testid="language-fr-btn"
                        >
                          <div className="option-flag">🇫🇷</div>
                          <div className="option-text">Français</div>
                        </button>
                        <button
                          onClick={() => updateSettings({ language: "en" })}
                          className={`option-card ${settings.language === "en" ? "active" : ""}`}
                          data-testid="language-en-btn"
                        >
                          <div className="option-flag">🇬🇧</div>
                          <div className="option-text">English</div>
                        </button>
                        <button
                          onClick={() => updateSettings({ language: "es" })}
                          className={`option-card ${settings.language === "es" ? "active" : ""}`}
                          data-testid="language-es-btn"
                        >
                          <div className="option-flag">🇪🇸</div>
                          <div className="option-text">Español</div>
                        </button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </>
      )}

      {/* Chat Area */}
      <div className="chat-area">
        {currentConversation ? (
          <>
            <div className="chat-header">
              <h2 data-testid="current-conversation-title">{currentConversation.title}</h2>
            </div>

            <div className="messages-container" data-testid="messages-container">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/9qig1fli_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
                    alt="Abena" 
                    className="empty-state-avatar"
                  />
                  <h3>{t.startConversation}</h3>
                  <p>{t.shareThoughts}</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.role}`}
                    data-testid={`message-${msg.role}-${msg.id}`}
                  >
                    <div className="message-avatar">
                      {msg.role === "user" ? (
                        <div className="user-avatar">👤</div>
                      ) : (
                        <img 
                          src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/ajxw7a5m_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
                          alt="Abena" 
                          className="ai-avatar"
                        />
                      )}
                    </div>
                    <div className="message-content">
                      {msg.image_base64 ? (
                        <div className="message-image-container">
                          <img 
                            src={`data:image/png;base64,${msg.image_base64}`}
                            alt="Generated" 
                            className="generated-image"
                          />
                          <p className="image-caption">{msg.content.replace('[IMAGE_GENERATED] ', '')}</p>
                        </div>
                      ) : (
                        <p data-testid={`message-content-${msg.id}`}>{msg.content}</p>
                      )}
                      {msg.role === "assistant" && !msg.image_base64 && (
                        <button
                          onClick={() => speakText(msg.content)}
                          className="speak-btn"
                          title="Écouter la réponse"
                          data-testid={`speak-btn-${msg.id}`}
                        >
                          {isSpeaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="message assistant" data-testid="loading-message">
                  <div className="message-avatar">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/9qig1fli_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
                      alt="Abena" 
                      className="ai-avatar"
                    />
                  </div>
                  <div className="message-content">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="input-area">
              <Button
                onClick={toggleRecording}
                className={`audio-btn ${isRecording ? 'recording' : ''}`}
                data-testid="voice-input-btn"
                title={isRecording ? "Arrêter l'enregistrement" : "Enregistrement vocal"}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && !loading && !generatingImage && handleMessageWithStreamingDetection()}
                placeholder={t.placeholder}
                disabled={loading || generatingImage}
                className="message-input"
                data-testid="message-input"
              />
              
              <Button
                onClick={handleMessageWithStreamingDetection}
                disabled={loading || generatingImage || !inputMessage.trim()}
                className="send-btn"
                data-testid="send-btn"
              >
                {generatingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/9qig1fli_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
              alt="Abena" 
              className="empty-state-avatar"
            />
            <h3>{t.welcome}</h3>
            <p>{t.createNewConv}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AuthScreen({ onLogin, onSignup, language, setLanguage }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [authMode, setAuthMode] = useState("login");

  const t = translations[language] || translations.fr;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (authMode === "login") {
      onLogin(email, password);
    } else {
      onSignup(email, password, name);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_ai-dialog-maker/artifacts/9qig1fli_ChatGPT%20Image%2019%20oct.%202025%2C%2001_49_21.png" 
            alt="Abena" 
            className="auth-avatar"
          />
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
          <div className="language-selector">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-32">
                <Languages className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">FR</SelectItem>
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="es">ES</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="auth-card">
          <CardHeader>
            <CardTitle>{authMode === "login" ? t.login : t.signup}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="auth-form">
              {authMode === "signup" && (
                <div className="form-group">
                  <label>{t.name}</label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="name-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label>{t.email}</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>
              <div className="form-group">
                <label>{t.password}</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
              </div>
              <Button type="submit" className="auth-submit-btn" data-testid="auth-submit-btn">
                {authMode === "login" ? t.loginBtn : t.signupBtn}
              </Button>
            </form>
            <div className="auth-toggle">
              <p>
                {authMode === "login" ? t.noAccount : t.haveAccount}
                <button onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")} data-testid="auth-toggle-btn">
                  {authMode === "login" ? t.signup : t.login}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default App;